/* Copyright (c) 2020-present CloudIO Inc */

import {
  atom,
  useStateXAtom,
  useStateXAtomSetter,
  useStateXGetter,
  useStateXQuietSetter,
  useStateXRefSetter,
  useStateXSetter,
  useStateXStore,
} from '@cloudio-saas/statex';
import { Alert, Box, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { PageRefContext, useEmbeddedContext } from 'common/Context';
import {
  BaseComponentItem,
  BaseProps,
  getManager,
  IOPage,
  ItemType,
  Page,
  Platform,
} from 'componentregistry';
import { getScrollbarSize } from 'components/DataGrid/grid/GridUtils';
import Controller from 'controller/Controller';
import { hideHeaderAtom } from 'designer/atoms';
import { useStringExpression } from 'expression/useExpression';
import { usePaddingBorder } from 'hooks/useMarginPadding';
import { extractDevProps } from 'platform/DevProps';
import ErrorBoundary from 'platform/ErrorBoundary';
import SimpleLoading from 'root/SimpleLoading';
import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import NotFound from 'routes/NotFound';
import {
  backgroundColorStyleFn,
  readableColorStyleFn,
} from 'theme/useThemeColor';
import { getWidthHeight } from 'utils/HeightWidthUtil';
import { subTitleAtom } from 'utils/state';
import { getURLParam } from 'utils/url';
import useJoinRef from 'utils/useJoinRef';
import { sidebarStateAtom } from '../../designer/state';
import { rootPageAtom, rootPageWidthAtom } from '../../hooks/useWindowSize';
import { useTranslate } from '../../utils/lang';
import WaveDots from '../../utils/WaveDots';
import { PageCSS } from './PageCSS';
import { onPageLoadEnd, pageInitializedSignal } from './PageProgressed';
import PageRendered from './PageRendered';
import PageTriggers from './PageTriggers';
import { useResponsiveTriggers } from './useResponsiveTriggers';
import { PageInitialized } from './PageInitialized';
import { useChildren } from 'registry/Components';
import { DataStoreItem } from 'componentregistry/components/DataStore/DataStore';
import { ReadOnlyDataStoreItem } from 'componentregistry/components/ReadOnlyDataStore/ReadOnlyDataStore';
import { LocalDataStoreItem } from 'componentregistry/components/LocalDataStore/LocalDataStore';
import { TransientDataStoreItem } from 'componentregistry/components/TransientDataStore/TransientDataStore';
import { CloudDataStoreItem } from 'componentregistry/components/CloudDataStore/CloudDataStore';
import { initStore } from 'store/useDataStore';

const useStyles = makeStyles((theme: Theme) => ({
  page: {
    backgroundColor: backgroundColorStyleFn(theme),
    color: readableColorStyleFn(theme),
    position: 'relative',
  },
}));

// used to delay the rendering of sidebar panels until all the stores are initialized
export const firstPageLoadedAtom = atom({
  path: ['transient', '$pageLoaded'],
  defaultValue: false,
});

export const appPageLoadedAtom = atom({
  path: ['transient', ':appUid', ':pageId', '$pageLoaded'],
  defaultValue: false,
});

interface Props extends Required<IOPage.IOPage>, BaseProps<'Page'> {
  paramString: string;
}

const ABSOLUTE_ITEMS: ItemType[] = [
  'FAB',
  'FloatingPanel',
  'SpeedDial',
  'SidebarPanel',
];

function isDataStore(
  item: BaseComponentItem,
): item is
  | CloudDataStoreItem
  | DataStoreItem
  | LocalDataStoreItem
  | ReadOnlyDataStoreItem
  | TransientDataStoreItem {
  return (
    [
      'CloudDataStore',
      'DataStore',
      'LocalDataStore',
      'ReadOnlyDataStore',
      'TransientDataStore',
    ] as ItemType[]
  ).includes(item.type);
}

function initStores(
  platform: Platform,
  appUid: string,
  pageId: string,
  page: Page,
  root: BaseComponentItem,
) {
  if (isDataStore(root)) {
    const {
      alias,
      dataSource,
      controlledBy,
      singleton = false,
      dsAppUid,
      displayName = '',
      hideDirtyIndicator = false,
      ignorePKDuplicate = false,
    } = root.props;
    if (
      alias &&
      dataSource &&
      !platform.isAliasRegistered({ appUid, pageId, alias })
    ) {
      platform.registerAlias({ appUid, pageId, alias, dataSource });
      const store = initStore({
        appUid,
        dsAppUid,
        pageId,
        ds: dataSource,
        alias,
        displayName,
        platform,
        programmatic: false,
        filterLocally: false,
        itemId: root.itemId,
        itemType: root.type,
        maxRows: 0,
        singleton,
        controlledBy,
      });
      store.programmatic = false;
      store.hideDirtyIndicator = hideDirtyIndicator;
      store.localStore = root.type === 'LocalDataStore';
      store.cloudStore = root.type === 'CloudDataStore';
      store.filterLocally = root.type === 'LocalDataStore';
      store.ignorePKDuplicate = ignorePKDuplicate;
    }
  }

  if (root.type === 'Folder' || root.type === 'Page') {
    root.children.forEach((c) => {
      initStores(platform, appUid, pageId, page, page[c]);
    });
    return;
  }
}

function PageIO(props: Props, ref: React.Ref<HTMLDivElement>) {
  const {
    appUid,
    roleUid,
    backgroundColor,
    border,
    className,
    domProps,
    floatingHeader,
    height: h,
    itemId,
    itemType,
    minHeight,
    minWidth,
    onPageActiveFunction,
    onPageExitFunction,
    padding,
    pageId,
    paramString,
    platform,
    width: w,
    windowTitle,
    withBootstrap,
    withTailwind,
  } = props;
  const { t } = useTranslate();
  // useWhyDidYouUpdate('Page', { ...props }, true);
  const setHideHeader = useStateXAtomSetter(hideHeaderAtom);
  const skipCustomCode = getURLParam('skipCustomCode') === 'Y';
  const { embedded, onRoot } = useEmbeddedContext();
  const store = useStateXStore();

  useLayoutEffect(() => {
    if (!embedded) {
      if (!store.hasStateInitializedFromURL(hideHeaderAtom.path)) {
        setHideHeader(floatingHeader);
      }
    }
  }, [embedded, floatingHeader, setHideHeader]);

  let width = Math.max(minWidth, w ?? 0);
  let height = Math.max(minHeight, h ?? 0);
  const ss = getScrollbarSize();
  if (h && minWidth < width && minHeight > h) {
    // prevent horizontal scrollbar when only vertical is needed
    width -= ss;
  }
  if (w && minHeight < height && minWidth > w) {
    // prevent vertical scrollbar when only horizontal is needed
    height -= ss;
  }
  const devProps = extractDevProps(props);
  const classes = useStyles({ backgroundColor });
  const set = useStateXSetter();
  const get = useStateXGetter();
  const setQuietly = useStateXQuietSetter();

  const initRef = useRef(false);
  if (!initRef.current) {
    initRef.current = true;
    const page = platform.getPage(appUid, pageId);
    // create the stores first before checking the visible
    // condition of children of the page root item (in useResponsiveTriggers & useChildren)
    initStores(platform, appUid, pageId, page, page.root);
  }

  let titleResult =
    useStringExpression({
      exp: windowTitle,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'windowTitle',
        data: {},
      },
    }) ?? '';

  if (windowTitle.type === 'text') {
    titleResult = t(titleResult);
  }

  useResponsiveTriggers({ platform, roleUid, appUid, pageId, itemId });
  const children = useChildren({ appUid, pageId, itemId, itemType });

  useEffect(() => {
    if (!onRoot && titleResult) {
      const oldTitle = get(subTitleAtom);
      if (titleResult && oldTitle !== titleResult) {
        window.document.title = titleResult;
        set(subTitleAtom, titleResult);
        return () => {
          window.document.title = oldTitle;
          set(subTitleAtom, oldTitle);
        };
      }
    }
  }, [onRoot, titleResult]);

  const propsRef = useRef(props);

  useEffect(() => {
    if (skipCustomCode) return;
    const _props = propsRef.current;
    return () => {
      Controller.callAsyncIfExists({
        platform,
        appUid,
        pageId,
        functionName: onPageExitFunction,
        itemId,
        props: { ..._props },
      });
    };
  }, [appUid, itemId, onPageExitFunction, pageId, platform, skipCustomCode]);

  useEffect(() => {
    if (skipCustomCode) return;
    const _props = propsRef.current;
    Controller.callAsyncIfExists({
      platform,
      appUid,
      pageId,
      functionName: onPageActiveFunction,
      itemId,
      props: { ..._props },
    });
  }, [appUid, itemId, onPageActiveFunction, pageId, platform, skipCustomCode]);

  const setRef = useStateXRefSetter();
  const localRef = useRef<HTMLDivElement>(null);
  const joinRef = useJoinRef(ref, localRef);
  const styles = usePaddingBorder(padding, border);
  const [sidebarState, setSidebarState] = useStateXAtom(sidebarStateAtom, {
    params: { appUid },
  });
  const {
    innerHeight,
    innerWidth: _innerWidth,
    outerHeight,
    outerWidth,
    innerLeft,
  } = getWidthHeight(height, width, padding, border);

  useEffect(() => {
    if (!embedded) setSidebarState((s) => ({ ...s, padding: padding }));
  }, [embedded, padding]);

  let innerWidth = _innerWidth;
  const hasSideBar =
    sidebarState?.exists && sidebarState.open && innerWidth && !embedded;
  if (hasSideBar && innerWidth) {
    innerWidth -= sidebarState.width;
  }
  const rootStyle: CSSProperties = {
    ...styles,
    height: h,
    width: w,
    overflowY: (h ?? 0) < (outerHeight ?? 0) ? 'auto' : 'hidden',
    overflowX: (w ?? 0) < (outerWidth ?? 0) ? 'auto' : 'hidden',
    position: 'relative',
    paddingLeft: hasSideBar ? sidebarState.width + innerLeft : innerLeft,
  };

  useLayoutEffect(() => {
    if (!embedded) {
      if (platform.isRecording()) {
        platform.recordLine(itemId, `await ui.waitForPage('${pageId}');`);
      }

      setRef(['pageRef'], localRef);
      return () => {
        setRef(['pageRef'], undefined);
      };
    }
  }, [embedded, itemId, localRef, pageId, platform, setRef]);

  if (!embedded && innerWidth) {
    setQuietly(rootPageWidthAtom, innerWidth);
  }

  const rootPageAtomValue = useMemo(
    () => ({ pageId, appUid }),
    [appUid, pageId],
  );

  if (!embedded) {
    setQuietly(rootPageAtom, rootPageAtomValue);
  }

  useEffect(() => {
    set(firstPageLoadedAtom, true);
    set(appPageLoadedAtom, true, { params: { appUid, pageId } });
  }, [appUid, pageId]);

  const [opacity, setOpacity] = useState(
    pageInitializedSignal[appUid][pageId].peek() ? 1 : 0,
  );

  useEffect(() => {
    if (opacity === 1) return;
    return onPageLoadEnd(appUid, pageId, () => {
      setOpacity(1);
    });
  }, [appUid, pageId, opacity]);
  // console.log('Page.render', pageId);
  // useLayoutEffect(() => {
  //   console.log('Page.useLayoutEffect', pageId);
  // });
  // useEffect(() => {
  //   console.log('Page.useEffect', pageId);
  // });

  const key = getURLParam('$testRunKey$') ?? pageId;

  const pageHeader = Array.isArray(children)
    ? children.filter((c) => c.props.itemType === 'PageHeader')
    : null;

  if (embedded && onRoot) {
    let valid = true;
    let mgrName = '';
    React.Children.forEach<ReactNode>(children, (child) => {
      if (React.isValidElement<BaseProps<never>>(child)) {
        const _itemType = child.props.itemType;
        const mgr = getManager(_itemType);
        if (
          mgr.managerType === 'Component' &&
          !(
            [
              'Comments',
              'DataStore',
              'Drawer',
              'FAB',
              'FloatingPanel',
              'Folder',
              'LocalDataStore',
              'PageHeader',
              'Popup',
              'ReadOnlyDataStore',
              'SidebarPanel',
              'SpeedDial',
              'TransientDataStore',
            ] as ItemType[]
          ).includes(_itemType)
        ) {
          valid = false;
          mgrName = mgr.name;
        }
      }
    });
    if (!valid) {
      return (
        <Alert severity="error">
          Root level embedded page must not contain layout components such as{' '}
          {mgrName}!
        </Alert>
      );
    }
    const otherChildren = Array.isArray(children)
      ? children.filter((c) => c.props.itemType !== 'PageHeader')
      : null;

    return (
      <div className="embed-page-root" ref={joinRef}>
        {pageHeader?.length
          ? React.Children.map<ReactNode, ReactNode>(pageHeader, (child) => {
              if (React.isValidElement<BaseProps<never>>(child)) {
                return React.cloneElement(child, {
                  width: outerWidth,
                  height: outerHeight,
                });
              }
              return child;
            })
          : null}
        {otherChildren}
      </div>
    );
  }

  const otherChildren = Array.isArray(children)
    ? children.filter(
        (c) =>
          !ABSOLUTE_ITEMS.includes(c.props.itemType) &&
          c.props.itemType !== 'PageHeader',
      )
    : null;

  const absolutionChildren = Array.isArray(children)
    ? children.filter((c) => ABSOLUTE_ITEMS.includes(c.props.itemType))
    : null;

  return (
    <PageCSS
      appUid={appUid}
      pageId={pageId}
      key={key}
      withTailwind={withTailwind}
      withBootstrap={withBootstrap}
    >
      {opacity === 0 ? (
        <Box
          sx={{
            height: h,
            width: w,
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <WaveDots active />
        </Box>
      ) : null}
      <Box
        ref={joinRef}
        className={clsx(classes.page, className, pageId)}
        style={rootStyle}
        {...domProps}
        {...devProps}
        onDragStartCapture={(event) => {
          let el: HTMLElement | null = event.target as HTMLElement;
          while (el) {
            if (el.className.indexOf('nodrag') !== -1) {
              event.preventDefault();
            }
            el = el.parentElement;
          }
        }}
        sx={{
          transition: 'opacity 1s ease-out',
          opacity: opacity,
        }}
      >
        {titleResult === '404' && React.Children.count(children) === 0 ? (
          <NotFound reason="Page not found!" />
        ) : (
          <ErrorBoundary platform={platform}>
            <PageRefContext.Provider
              value={{
                pageRef: localRef,
                pageHeight: innerHeight ?? 0,
                pageWidth: innerWidth ?? 0,
              }}
            >
              {width && height ? (
                <>
                  {React.Children.map<ReactNode, ReactNode>(
                    otherChildren,
                    (child) => {
                      if (
                        React.isValidElement<
                          BaseProps<never> & { onRoot?: boolean }
                        >(child)
                      ) {
                        if (
                          (
                            [
                              'DataStore',
                              'ReadOnlyDataStore',
                              'TransientDataStore',
                              'LocalDataStore',
                            ] as ItemType[]
                          ).includes(child.props.itemType)
                        ) {
                          return child;
                        }

                        if (
                          (['EmbedPage'] as ItemType[]).includes(
                            child.props.itemType,
                          )
                        ) {
                          return React.cloneElement(child, {
                            onRoot: true,
                            width: outerWidth,
                            height: outerHeight,
                          });
                        }

                        return React.cloneElement(child, {
                          width: innerWidth,
                          height: innerHeight,
                        });
                      }
                      return child;
                    },
                  )}
                  {React.Children.map<ReactNode, ReactNode>(
                    absolutionChildren,
                    (child) => {
                      if (React.isValidElement<BaseProps<never>>(child)) {
                        return React.cloneElement(child, {
                          width: innerWidth,
                          height: innerHeight,
                        });
                      }
                      return child;
                    },
                  )}
                  {React.Children.map<ReactNode, ReactNode>(
                    pageHeader,
                    (child) => {
                      if (React.isValidElement<BaseProps<never>>(child)) {
                        return React.cloneElement(child, {
                          width: outerWidth,
                          height: outerHeight,
                        });
                      }
                      return child;
                    },
                  )}
                  <PageInitialized appUid={appUid} pageId={pageId} />
                </>
              ) : null}
            </PageRefContext.Provider>
          </ErrorBoundary>
        )}
      </Box>
      <React.Suspense fallback={<SimpleLoading name="PageRendered" />}>
        <PageRendered
          appUid={appUid}
          pageId={pageId}
          paramString={paramString}
        />
      </React.Suspense>
      <PageTriggers
        appUid={appUid}
        pageId={pageId}
        itemId={itemId}
        platform={platform}
        paramString={paramString}
        roleUid={roleUid}
      />
    </PageCSS>
  );
}

export default React.forwardRef(PageIO);
